import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Page,
  Card,
  IndexTable,
  Text,
  Button,
  InlineStack,
  BlockStack,
  Badge,
  EmptyState,
  Banner,
  Modal,
  Box,
  TextField,
  Icon,
  Popover,
  ActionList,
} from "@shopify/polaris";
import { EditIcon, DeleteIcon, SearchIcon, FilterIcon } from "@shopify/polaris-icons";
import { fetchTemplates, deleteTemplate } from "../../api/templatesApi";
import { getPlacementLabel } from "../../utils/themeEditorLinks";
import {
  TEMPLATE_FILTER_OPTIONS,
  filterLabel,
  filterTemplateRows,
  searchTemplateRows,
} from "../../utils/templatesIndex";
import { useAppTitleBar } from "../../hooks/useAppTitleBar";
import { ListPageSkeleton } from "../../components/PageSkeleton";

export default function BarsListPage({
  title = "Shipping bars",
  createLabel = "Create bar",
  resourceName = { singular: "bar", plural: "bars" },
  emptyHeading = "No shipping bars yet",
  deleteTitle = "Delete bar?",
} = {}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: "" });
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const load = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    setError(null);
    if (!silent) setLoading(true);
    try {
      const res = await fetchTemplates();
      const list = res?.data?.templates ?? [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || "Failed to load bars");
      setRows([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleRows = useMemo(() => {
    let next = filterTemplateRows(rows, filter);
    next = searchTemplateRows(next, search);
    return next.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [rows, filter, search]);

  const onCreate = useCallback(() => {
    navigate("/onboarding/choose");
  }, [navigate]);

  const confirmDelete = useCallback(async () => {
    const delId = deleteModal.id;
    if (delId == null) return;
    setDeleteSubmitting(true);
    try {
      await deleteTemplate(delId);
      setDeleteModal({ open: false, id: null, name: "" });
      await load({ silent: true });
    } catch (e) {
      setError(e?.message || "Delete failed");
    } finally {
      setDeleteSubmitting(false);
    }
  }, [deleteModal.id, load]);

  const filterItems = TEMPLATE_FILTER_OPTIONS.map((opt) => ({
    content: opt.label,
    active: filter === opt.value,
    onAction: () => {
      setFilter(opt.value);
      setFilterOpen(false);
    },
  }));

  const resourceSingular = resourceName.singular || "bar";
  const resourcePlural = resourceName.plural || "bars";

  useAppTitleBar({
    title,
    primaryAction: { label: createLabel, onClick: onCreate },
  });

  return (
    <Page>
      <BlockStack gap="500">
        {error ? (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        ) : null}

        <Card padding="0">
          {loading ? (
            <ListPageSkeleton />
          ) : rows.length === 0 ? (
            <Box paddingBlockStart="400" paddingBlockEnd="600">
              <EmptyState heading={emptyHeading} action={{ content: createLabel, onAction: onCreate }}>
                <p>Choose a placement and set your free-shipping goal to get started.</p>
              </EmptyState>
            </Box>
          ) : (
            <>
              <Box padding="400">
                <InlineStack gap="300" wrap blockAlign="center">
                  <div style={{ flex: "1 1 280px", minWidth: 220 }}>
                    <TextField
                      label={`Search ${resourcePlural}`}
                      labelHidden
                      placeholder={`Search name, placement, goal, status…`}
                      value={search}
                      onChange={setSearch}
                      prefix={<Icon source={SearchIcon} />}
                      autoComplete="off"
                      clearButton
                      onClearButtonClick={() => setSearch("")}
                    />
                  </div>
                  <Popover
                    active={filterOpen}
                    onClose={() => setFilterOpen(false)}
                    activator={
                      <Button icon={FilterIcon} onClick={() => setFilterOpen((v) => !v)} disclosure>
                        {filterLabel(filter)}
                      </Button>
                    }
                  >
                    <ActionList actionRole="menuitemradio" items={filterItems} />
                  </Popover>
                </InlineStack>
                <Box paddingBlockStart="200">
                  <Text as="p" variant="bodySm" tone="subdued">
                    Showing {visibleRows.length} of {rows.length} {rows.length === 1 ? resourceSingular : resourcePlural}
                  </Text>
                </Box>
              </Box>
              {visibleRows.length === 0 ? (
                <Box padding="600">
                  <Text as="p" tone="subdued" alignment="center">
                    No {resourcePlural} match your search or filter.
                  </Text>
                </Box>
              ) : (
                <IndexTable
                  resourceName={resourceName}
                  itemCount={visibleRows.length}
                  headings={[
                    { title: "Name" },
                    { title: "Placement" },
                    { title: "Goal" },
                    { title: "Status" },
                    { title: "Actions", alignment: "end" },
                  ]}
                  selectable={false}
                >
                  {visibleRows.map((row, index) => (
                    <IndexTable.Row id={String(row.id)} key={row.id} position={index}>
                      <IndexTable.Cell>
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="span" variant="bodyMd" fontWeight="semibold">
                            {row.name}
                          </Text>
                          {row.is_primary ? (
                            <Badge tone="info" size="small">
                              Primary
                            </Badge>
                          ) : null}
                        </InlineStack>
                      </IndexTable.Cell>
                      <IndexTable.Cell>{getPlacementLabel(row.placement_type)}</IndexTable.Cell>
                      <IndexTable.Cell>
                        ${row.goal_amount} {row.currency_code}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Badge tone={row.is_published ? "success" : "attention"} size="small">
                          {row.is_published ? "Published" : "Draft"}
                        </Badge>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <InlineStack gap="200" align="end">
                          <Button
                            variant="plain"
                            icon={EditIcon}
                            onClick={() => navigate(`/settings/bars/${row.id}`)}
                          />
                          <Button
                            variant="plain"
                            icon={DeleteIcon}
                            disabled={rows.length <= 1}
                            onClick={() =>
                              setDeleteModal({ open: true, id: row.id, name: row.name || "Bar" })
                            }
                          />
                        </InlineStack>
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  ))}
                </IndexTable>
              )}
            </>
          )}
        </Card>
      </BlockStack>

      <Modal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null, name: "" })}
        title={deleteTitle}
        primaryAction={{
          content: "Delete",
          destructive: true,
          loading: deleteSubmitting,
          onAction: () => void confirmDelete(),
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setDeleteModal({ open: false, id: null, name: "" }) },
        ]}
      >
        <Modal.Section>
          <Text as="p">
            Delete <strong>{deleteModal.name}</strong>? This cannot be undone.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
